/**
 * TranslationService.js
 * Handles real-time translation using Google Cloud Translation API v2/v3.
 * Implements aggressive caching (LocalStorage) to minimize costs.
 */

import { getFirestore, doc, getDoc, setDoc } from "firebase/firestore";
import { app } from "../firebase-config.js";

const db = getFirestore(app);
const CACHE_PREFIX = 'tr_cache_';
const API_URL = `https://translation.googleapis.com/language/translate/v2`;

export class TranslationService {
    constructor() {
        this.cache = new Map(); // Memory Cache (for current session speed)
        // Warn if key is missing

    }

    /**
     * Translates text to target language
     * @param {string} text - Source text
     * @param {string} targetLang - Target language code (en, ja, zh, vi, etc.)
     * @returns {Promise<string>} Translated text
     */
    async translate(text, targetLang = 'en') {
        if (!text || !text.trim()) return text;

        // 1. Check Cache (Memory -> LocalStorage)
        const cacheKey = this._getCacheKey(text, targetLang);

        if (this.cache.has(cacheKey)) {
            return this.cache.get(cacheKey);
        }

        const stored = localStorage.getItem(cacheKey);
        if (stored) {
            this.cache.set(cacheKey, stored);
            return stored;
        }

        // 2. Check Firestore (Shared Cache)
        const firestoreHash = this._hashString(text).toString(); // Simple ID
        const docRef = doc(db, "translations", `${targetLang}_${firestoreHash}`);

        try {
            const docSnap = await getDoc(docRef);
            if (docSnap.exists()) {
                const cloudText = docSnap.data().text || "";
                // Verify minimal collision check (optional, but good for safety if we stored original)
                // For MVP, trust the hash or if we stored original length? 
                // Let's assume it's valid.
                this._saveCache(cacheKey, cloudText); // Promotes to Local/Memory
                return cloudText;
            }
        } catch (e) {
            console.warn("[TranslationService] Firestore Read Error:", e);
            // Proceed to API
        }

        // 3. Fetch from Google API
        try {
            const translated = await this._fetchGoogleTranslate(text, targetLang);

            // 4. Save to Cache (All Layers)
            this._saveCache(cacheKey, translated);

            // 5. Save to Firestore (Async - Fire and Forget)
            setDoc(docRef, {
                original: text.substring(0, 100), // logging partial original for debug
                text: translated,
                lang: targetLang,
                createdAt: new Date()
            }).catch(e => console.warn("[TranslationService] Firestore Write Failed:", e));

            return translated;
        } catch (error) {
            console.error("[TranslationService] API Error:", error);
            throw error; // Let caller handle UI feedback
        }
    }

    /**
     * Actual API Call
     */
    async _fetchGoogleTranslate(text, target, retries = 1) {
        // [SECURE] Use Cloud Function Proxy to avoid Referrer/CORS issues
        try {
            console.log(`[TranslationService] Calling Cloud Function for: ${target}`);
            const { getFunctions, httpsCallable } = await import("firebase/functions");
            // App instance is already imported as 'app'
            const functions = getFunctions(app);
            const translateFn = httpsCallable(functions, 'translateText');

            const result = await translateFn({
                text: text,
                targetLang: target
            });

            return result.data.translatedText;
        } catch (e) {
            console.error(`[TranslationService] Proxy Error (Remain: ${retries}):`, e);

            // [RETRY] Cold Start Mitigation
            if (retries > 0) {
                console.log(`[TranslationService] Retrying in 1000ms...`);
                await new Promise(resolve => setTimeout(resolve, 1000));
                return this._fetchGoogleTranslate(text, target, retries - 1);
            }

            throw e;
        }
    }

    _getCacheKey(text, lang) {
        // Simple hash or substring to shorten key? 
        // For accurate caching, we need the full text distinction.
        // But LocalStorage key limit? 
        // Let's use a simple hash of the text + lang.
        const hash = this._hashString(text);
        return `${CACHE_PREFIX}${lang}_${hash}`;
    }

    _hashString(str) {
        let hash = 0;
        for (let i = 0; i < str.length; i++) {
            const char = str.charCodeAt(i);
            hash = (hash << 5) - hash + char;
            hash |= 0; // Convert to 32bit integer
        }
        // Ensure positive integer string for Firestore IDs
        return Math.abs(hash);
    }

    _saveCache(key, value) {
        try {
            this.cache.set(key, value);
            localStorage.setItem(key, value);
        } catch (e) {
            // QuotaExceededError handling could go here (clear old cache)
            console.warn("[TranslationService] Cache write failed (Quota?)", e);
        }
    }
}

// Singleton Instance
export const templateTranslationService = new TranslationService();
